"use client"
import { footerSectionData_en } from "@/data/en/layout/footer/v1";
import { footerSectionData_ar } from "@/data/ar/layout/footer/v1";
import { ImageProps, LinkProps } from "@/src/common-types";
import { Container } from "@/src/components/container";
import { CustomLink } from "@/src/components/custom-link";
import { cn } from "@/src/utils/shadcn";
import { BrandLogo } from "src/layout/brand-logo";
import {
  FaChevronRight,
  FaEnvelope,
  FaLocationDot,
  FaPhone,
} from "react-icons/fa6";
import { IsoFooterSection } from "@/src/sections/iso-footer";
import { useEffect, useState } from "react";

interface SocialLinkProps {
  icon: React.ReactNode;
  href: string;
}

interface FooterSectionProps {
  about: {
    description: string;
    socialLinks: SocialLinkProps[];
  };
  columnOne: {
    title: string;
    links: LinkProps[];
  };
  columnTwo: {
    title: string;
    location: string;
    mails: string[];
    phoneNumbers: string[];
  };
  columnThree: {
    title: string;
    links: LinkProps[];
  };
  footerBottom: {
    copyrightText: string;
    links: LinkProps[];
  };
  params: { lang: string };
}

const socialIconClasses = cn(
  "text-base/[1.75] transition-transform duration-350 hover:-translate-y-1 block px-4"
);
const titleClasses = cn(
  "text-gray-900 dark:text-white  text-md font-bold  leading-[1.25] md:text-lg mb-5 md:mb-[1.875rem]"
);
const addressIconParentClasses = cn(
  "w-10 h-10 rounded-5 inline-grid place-items-center dark:bg-accent-700 border border-accent-800 dark:border-transparent text-primary flex-none"
);
const addressItemClasses = cn("flex items-center gap-5");
const textColor = cn(
  "transition-colors duration-300 hover:text-primary dark:hover:text-white"
);

export function Footer({
  className,
  params,
}: {
  className?: string;
  params: { lang: string };
}) {
  const isArabic = params?.lang === "ar";
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return null; // Ensure client-side rendering does not affect SSR
  }

  const { about, columnOne, columnTwo, columnThree, footerBottom } =
    isArabic ? footerSectionData_ar : footerSectionData_en;

  const chevronClass = isArabic ? "rotate-180" : "";

  return (
    <footer
      className={cn(
        "overflow-hidden bg-accent-100 text-accent-800 dark:bg-accent-900 dark:text-body",
        className
      )}
    >
      <div className="py-16 md:py-20">
        <Container>
          <div className="grid gap-10 md:grid-cols-2 xl:grid-cols-4">
            {/* About Section */}
            <AboutSection about={about} socialIconClasses={socialIconClasses} />

            {/* Column One */}
            <ColumnSection
              title={columnOne.title}
              links={columnOne.links}
              chevronClass={chevronClass}
              isArabic={isArabic}
              isServices={!isArabic ? true : false}
            />

            {/* Column Three services */}
            <ColumnSection
              title={columnThree.title}
              links={columnThree.links}
              chevronClass={chevronClass}
              isArabic={isArabic}
              isServices={isArabic ? true : false}
            />
            {
              console.log('columnThree', columnThree)
            }

            {/* Column Two (Contact Info) */}
            <ContactSection columnTwo={columnTwo} />
          </div>
        </Container>
      </div>

      <Container>
        <IsoFooterSection params={params} />
      </Container>

      {/* Footer Bottom Section */}
      <FooterBottom footerBottom={footerBottom} />
    </footer>
  );
}

// About Section Component
const AboutSection = ({
  about,
  socialIconClasses,
}: {
  about: FooterSectionProps["about"];
  socialIconClasses: string;
}) => {
  return (
    <div id="footer" data-aos="fade-up" data-aos-delay="200">
      <BrandLogo />
      <p className="mb-7 mt-3">{about.description}</p>
      {about.socialLinks && about.socialLinks.length > 0 && (
        <nav aria-label="social links">
          <ul dir="ltr" className="inline-flex min-h-[50px] items-center divide-x rounded-5 bg-primary text-white">
            {about.socialLinks.map((socialLink, index) => (
              <li key={index}>
                <CustomLink
                  aria-label={socialLink.href}
                  className={socialIconClasses}
                  href={socialLink.href}
                  openNewTab
                >
                  <span>{socialLink.icon}</span>
                </CustomLink>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};

// Column Section Component
const ColumnSection = ({
  title,
  links,
  chevronClass,
  isArabic,
  isServices
}: {
  title: string;
  links: LinkProps[];
  chevronClass: string;
  isArabic: boolean;
  isServices: boolean;
}) => (
  <div data-aos="fade-up" data-aos-delay="400">
    <h3 className={titleClasses}>{title}</h3>
    {links && links.length > 0 && (
      <nav aria-label="footer links navigation">
        <ul className="grid gap-2">
          {links.map((link, index) => (
            <li key={link.label} className="flex items-center gap-2.5">
              <span
                className={`flex-none text-sm/[1] text-gray-900 dark:text-white ${chevronClass}`}
              >
                <FaChevronRight />
              </span>
              <CustomLink
                href={isServices ?
                  `${`/${isArabic ? 'ar' : 'en'}/services#${index}`}`
                  :
                  `${`/${isArabic ? 'ar' : 'en'}/solutions/single/` + link.label + "/" + link.id}`}
                openNewTab={link.openNewTab}
                className={textColor}
              >
                {link.label}
              </CustomLink>
            </li>
          ))}
        </ul>
      </nav>
    )}
  </div>
);

// Contact Section Component
const ContactSection = ({
  columnTwo,
}: {
  columnTwo: FooterSectionProps["columnTwo"];
}) => (
  <div data-aos="fade-up" data-aos-delay="600">
    <h3 className={titleClasses}>{columnTwo.title}</h3>
    <ul aria-label="addresses" className="grid gap-5">
      <li className={addressItemClasses}>
        <span className={addressIconParentClasses}>
          <FaLocationDot />
        </span>
        <address className="not-italic">{columnTwo.location}</address>
      </li>
      <li className={addressItemClasses}>
        <span className={addressIconParentClasses}>
          <FaEnvelope />
        </span>
        {columnTwo.mails && columnTwo.mails.length > 0 && (
          <div className="grid gap-1">
            {columnTwo.mails.map((mail, index) => (
              <a key={index} href={`mailto:${mail}`} className={textColor}>
                {mail}
              </a>
            ))}
          </div>
        )}
      </li>
      <li className={addressItemClasses}>
        <span className={addressIconParentClasses}>
          <FaPhone />
        </span>
        {columnTwo.phoneNumbers && columnTwo.phoneNumbers.length > 0 && (
          <div className="grid gap-1" dir="ltr">
            {columnTwo.phoneNumbers.map((phoneNumber, index) => (
              <a
                key={index}
                href={`tel:${phoneNumber.split(" ").join("")}`}
                className={textColor}
              >
                {phoneNumber}
              </a>
            ))}
          </div>
        )}
      </li>
    </ul>
  </div>
);

// Footer Bottom Component
const FooterBottom = ({
  footerBottom,
}: {
  footerBottom: FooterSectionProps["footerBottom"];
}) => (
  <div className="flex min-h-[90px] items-center border-t border-accent-800 border-opacity-20 py-5 dark:border-body dark:border-opacity-20">
    <Container>
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4 md:gap-x-10">
        <p>{footerBottom.copyrightText}</p>
        {footerBottom.links && footerBottom.links.length > 0 && (
          <nav aria-label="footer bottom navigation">
            <ul className="flex flex-wrap items-center gap-x-4  md:gap-x-7">
              {footerBottom.links.map((link) => (
                <li key={link.label}>
                  <CustomLink
                    aria-label={`Go to page ${link.label}`}
                    href={link.href}
                    openNewTab={link.openNewTab}
                    className={textColor}
                  >
                    {link.label}
                  </CustomLink>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </Container>
  </div>
);
